/**
 * Contents of the popup shown when a drawn aircraft track is clicked.
 *
 * Built as a DOM node rather than an HTML string so the dismiss button can
 * carry a real handler — and so the ESN is inserted as text, never parsed as
 * markup.
 */

import { dateTimeInNwt } from '../../../utils/nwtDate';

/** One reported position, as the breadcrumb popup receives it (#812). */
export interface BreadcrumbPopupOptions {
  /** Feet above sea level. */
  alt: number | null;
  /** Knots. */
  speed: number | null;
  /** Degrees. */
  heading: number | null;
  /** ISO instant of the observation. */
  t: string | null;
}

const EM_DASH = '—';

/**
 * A reading that was never taken reads as a dash, never as zero — an aircraft
 * with no reported speed is not an aircraft holding at 0 kt. A genuine zero is
 * a real measurement and prints as one.
 */
const reading = (value: number | null, suffix: string): string =>
  value === null || value === undefined || Number.isNaN(value)
    ? EM_DASH
    : `${value.toLocaleString('en-CA')}${suffix}`;

/**
 * Contents of the popup shown when a breadcrumb on a drawn track is clicked
 * (#812).
 *
 * Altitude is labelled ASL on its face. The request and #809 both asked for
 * height above ground level; Selkirk transmits altitude above sea level only
 * and AGL cannot be derived from it, so the reference is stated rather than
 * left for the reader to assume — the two differ by the ground elevation
 * beneath the aircraft, which over the NWT is several hundred feet.
 */
export function buildBreadcrumbPopupContent(options: BreadcrumbPopupOptions): HTMLElement {
  const root = document.createElement('div');
  root.className = 'aff-breadcrumb-popup';

  const title = document.createElement('h4');
  title.textContent = 'Reported position';
  root.appendChild(title);

  const rows: Array<[string, string]> = [
    ['Altitude', options.alt === null ? EM_DASH : `${reading(options.alt, '')} ft ASL`],
    ['Speed', options.speed === null ? EM_DASH : `${reading(options.speed, '')} kt`],
    ['Heading', options.heading === null ? EM_DASH : `${reading(options.heading, '')}°`],
    ['Observed', dateTimeInNwt(options.t) ?? EM_DASH],
  ];

  for (const [label, value] of rows) {
    const line = document.createElement('p');
    // textContent throughout: these values come from the database and from
    // Selkirk, and are data rather than markup.
    line.textContent = `${label}: ${value}`;
    root.appendChild(line);
  }

  return root;
}

export interface TrackPopupOptions {
  esn: string;
  hours: number;
  pointCount: number;
  onDismiss: () => void;
}

export function buildTrackPopupContent(options: TrackPopupOptions): HTMLElement {
  const root = document.createElement('div');
  root.className = 'aff-track-popup';

  const title = document.createElement('h4');
  title.textContent = 'Aircraft Track';
  root.appendChild(title);

  const esnLine = document.createElement('p');
  // textContent, not innerHTML — the esn comes from the database and is not markup.
  esnLine.textContent = `ESN: ${options.esn}`;
  root.appendChild(esnLine);

  const windowLine = document.createElement('p');
  // Previously hardcoded to "Last 48 hours", which misreported every other
  // duration the user could pick.
  windowLine.textContent = `Last ${options.hours} hours · ${options.pointCount} points`;
  root.appendChild(windowLine);

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'aff-track-popup__dismiss';
  dismiss.textContent = 'Dismiss track';
  dismiss.addEventListener('click', () => options.onDismiss());
  root.appendChild(dismiss);

  return root;
}
